import { useEffect, useState, useRef, useMemo, useCallback, memo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Fill, Stroke, Text } from 'ol/style';
import Overlay from 'ol/Overlay';
import { unByKey } from 'ol/Observable';

// Helper function to robustly compare two values (handles types, nulls, and case for strings)
const robustCompare = (val1, val2) => {
  if (val1 === null && val2 === null) return true;
  if (val1 === undefined && val2 === undefined) return true;
  if (val1 == null || val2 == null) return false; // One is null/undefined, the other isn't

  const type1 = typeof val1;
  const type2 = typeof val2;

  if (type1 === 'number' && type2 === 'number') return val1 === val2;
  // For all other combinations (string-string, string-number, number-string),
  // convert to string and compare case-insensitively.
  return String(val1).toLowerCase() === String(val2).toLowerCase();
};

// Helper function to convert hex color to RGB
const hexToRgb = (hex) => {
  // Remove the hash if it exists
  hex = hex.replace('#', '');
  
  // Handle shorthand hex (e.g., #FFF)
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  // Convert hex to rgb
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `${r}, ${g}, ${b}`;
};

// Memoized color map creation
const createColorMap = (legend) => {
  const map = new Map();
  if (Array.isArray(legend)) {
    legend.forEach(item => {
      if (item.value !== undefined && item.color) {
        map.set(item.value, item.color);
      }
    });
  }
  return map;
};

// Memoized severity map creation
const createSeverityMap = (mapAnalysisData, adminCodeProperty = 'adm1_pcode') => {
  const map = new Map();
  if (!mapAnalysisData) return map;

  try {
    let parsedData;
    
    // Handle different data formats - WebSocket sends objects, API might send strings
    if (typeof mapAnalysisData === 'string') {
      parsedData = JSON.parse(mapAnalysisData);
    } else if (mapAnalysisData.content) {
      // Content can be string (from API) or object (from WebSocket)
      if (typeof mapAnalysisData.content === 'string') {
        parsedData = JSON.parse(mapAnalysisData.content);
      } else if (typeof mapAnalysisData.content === 'object') {
        parsedData = mapAnalysisData.content;
      } else {
        parsedData = mapAnalysisData;
      }
    } else {
      parsedData = mapAnalysisData;
    }

    // Ensure we have data array
    const dataArray = parsedData.data || [];
    
    // Create severity map
    dataArray.forEach(item => {
      const adminCode = item[adminCodeProperty];
      if (adminCode) {
        const severityValue = item.value !== undefined ? item.value : 
                            (item.category !== undefined ? item.category : 
                            (item.severity_score !== undefined ? item.severity_score : null));
        if (severityValue !== null) {
          map.set(adminCode, severityValue); // Store the original value
        }
      }
    });
  } catch (error) {
    console.error('Error creating severity map:', error);
  }
  return map;
};

/**
 * Component to display admin1 boundaries as a polygon layer
 */
const Admin1Layer = memo(({
  map,
  geoJsonData,
  zIndex = 5,
  opacity = 0.75,
  showLabels = true,
  mapAnalysisData = null,
  hoveredSeverity = null,
  hoveredPcode = null,
  onHoverPcode = null,
  isMouseOverLegend = false,
  selectedPillarKey = null,
  mapUpdatedAt = null,
  hideTooltipRef
}) => {
  // Add this to track map analysis data changes
  const [currentMapAnalysisId, setCurrentMapAnalysisId] = useState(null);
  
  // Update the ID when mapAnalysisData changes for proper re-rendering
  useEffect(() => {
    if (mapAnalysisData) {
      let newId;
      try {
        // Try to get a consistent ID regardless of data structure
        if (mapAnalysisData.id) {
          newId = mapAnalysisData.id;
        } else if (typeof mapAnalysisData === 'object' && mapAnalysisData.content) {
          if (typeof mapAnalysisData.content === 'string') {
            // Ensure we're actually parsing JSON content when it's a string
            try {
              const parsed = JSON.parse(mapAnalysisData.content);
              newId = parsed.id || JSON.stringify(parsed).substring(0, 50);
            } catch (e) {
              // If parsing fails, just use the string for ID
              newId = mapAnalysisData.content.substring(0, 50);
            }
          } else {
            // Content is already an object
            newId = JSON.stringify(mapAnalysisData.content).substring(0, 50);
          }
        } else {
          // Last resort - stringify the whole object
          newId = JSON.stringify(mapAnalysisData).substring(0, 50);
        }
        
        // Add timestamp to ensure uniqueness for consecutive identical updates
        newId = `${newId}_${Date.now()}`;
        setCurrentMapAnalysisId(newId);
      } catch (error) {
        console.error('Error generating mapAnalysisId:', error);
        // Use timestamp as fallback if all else fails
        newId = `fallback_${Date.now()}`;
        setCurrentMapAnalysisId(newId);
      }
    } else {
      setCurrentMapAnalysisId(null);
    }
  }, [mapAnalysisData]);
  
  const [vectorLayer, setVectorLayer] = useState(null);
  const [source, setSource] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipListeners, setTooltipListeners] = useState([]);
  const [hoveredFeatureId, setHoveredFeatureId] = useState(null);

  // Effect to handle legend mouse interactions
  useEffect(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
      tooltipRef.current.style.visibility = 'hidden';
      tooltipRef.current.innerHTML = '';
    }
    
    if (map && map.getTargetElement()) {
      map.getTargetElement().style.cursor = '';
    }
    
    if (isMouseOverLegend) {
      setHoveredFeatureId(null);
      if (vectorLayer) {
        vectorLayer.changed();
      }
    }
  }, [isMouseOverLegend, map, vectorLayer]);
  
  // Update vector layer style when isMouseOverLegend changes
  useEffect(() => {
    if (vectorLayer) {
      vectorLayer.changed();
    }
  }, [isMouseOverLegend, vectorLayer]);
  
  // Effect to highlight features when hovering over legend items
  useEffect(() => {
    if (!source || !vectorLayer) return;
    
    // Reset hover state when no severity is hovered or mouse is not over legend
    if (hoveredSeverity === null || !isMouseOverLegend) { // Strict check for null
      setHoveredFeatureId(null);
      vectorLayer.changed();
      return;
    }
    
    // Find all features with the hovered severity
    const features = source.getFeatures();
    const matchingFeatures = features.filter(feature => {
      const properties = feature.getProperties();
      const adminCode = properties.adm1_pcode || properties.admin1_pcode || properties.admin1_id || properties.pcode;
      
      if (!adminCode || !mapAnalysisData) return false;
      
      try {
        let parsedData = mapAnalysisData;
        if (typeof mapAnalysisData === 'string') {
          parsedData = JSON.parse(mapAnalysisData);
        } else if (mapAnalysisData.content) {
          parsedData = typeof mapAnalysisData.content === 'string' 
            ? JSON.parse(mapAnalysisData.content) 
            : mapAnalysisData.content;
        }
        
        let dataArray = [];
        let legendArray = [];

        if (parsedData.data) dataArray = parsedData.data;
        if (parsedData.legend && Array.isArray(parsedData.legend)) legendArray = parsedData.legend;
        
        const adminData = dataArray.find(item => {
          const itemCode = item.admin1_pcode || item.admin1_id || item.pcode;
          return itemCode === adminCode || 
                (adminCode?.startsWith('AF') && itemCode === adminCode.substring(2)) ||
                (itemCode?.startsWith('AF') && adminCode === itemCode.substring(2));
        });
        
        if (adminData) {
          const currentFeatureSeverity = adminData.value !== undefined ? adminData.value : 
                              (adminData.category !== undefined ? adminData.category : 
                              (adminData.severity_score !== undefined ? adminData.severity_score : null));

          if (currentFeatureSeverity === null) return false; // Feature has no severity to compare

          // Find the target legend item that hoveredSeverity refers to
          const targetLegendItem = legendArray.find(legItem =>
            robustCompare(legItem.id, hoveredSeverity) ||
            robustCompare(legItem.value, hoveredSeverity) ||
            robustCompare(legItem.title, hoveredSeverity)
          );

          if (targetLegendItem) {
            // Compare currentFeatureSeverity against all relevant properties of the targetLegendItem
            return robustCompare(currentFeatureSeverity, targetLegendItem.id) ||
                   robustCompare(currentFeatureSeverity, targetLegendItem.value) ||
                   robustCompare(currentFeatureSeverity, targetLegendItem.title);
          } else {
            // Fallback: if hoveredSeverity couldn't be resolved to a specific legend item,
            // perform a direct comparison. This might occur if legend is malformed or hoveredSeverity is unexpected.
            return robustCompare(currentFeatureSeverity, hoveredSeverity);
          }
        }
      } catch (e) {
        // console.error('Error in matchingFeatures filter:', e);
        return false;
      }
      
      return false;
    });
    
    // Set the hovered feature IDs
    if (matchingFeatures.length > 0) {
      // Just highlight the first matching feature for now
      setHoveredFeatureId(matchingFeatures[0].getId() || matchingFeatures[0].ol_uid);
    } else {
      setHoveredFeatureId(null);
    }
    
    // Force a redraw of the layer
    vectorLayer.changed();
  }, [hoveredSeverity, source, mapAnalysisData, vectorLayer, isMouseOverLegend]);
  
  // Create tooltip overlay
  useEffect(() => {
    if (!map) return;
    
    // Clean up any existing tooltip
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }
    
    // Create tooltip element if it doesn't exist
    if (!tooltipRef.current) {
      const tooltipElement = document.createElement('div');
      tooltipElement.className = 'ol-tooltip';
      tooltipElement.style.display = 'none';
      tooltipElement.style.position = 'fixed'; // Use fixed positioning instead of absolute
      tooltipElement.style.zIndex = '99999'; // Ensure high z-index
      
      // Append directly to body to avoid any clipping issues with map container
      document.body.appendChild(tooltipElement);
      tooltipRef.current = tooltipElement;
    }
    
    // We'll handle positioning manually instead of using an overlay
    // This gives us more control over tooltip placement
    const tooltipOverlay = {
      setPosition: () => {}, // Dummy function, we'll position manually
      setPositioning: () => {}, // Dummy function
      setOffset: () => {} // Dummy function
    };
    
    // Store the overlay reference
    setTooltip(tooltipOverlay);
    
    return () => {
      // Hide tooltip
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
        tooltipRef.current.style.visibility = 'hidden';
        tooltipRef.current.innerHTML = ''; // Clear content
      }
      
      // Reset cursor if needed
      if (map && map.getTargetElement()) {
        map.getTargetElement().style.cursor = '';
      }
      
      // Clear any existing tooltip listeners
      tooltipListeners.forEach(listener => unByKey(listener));
      setTooltipListeners([]);
      
      // Clean up tooltip element when component unmounts
      if (tooltipRef.current && tooltipRef.current.parentNode) {
        tooltipRef.current.parentNode.removeChild(tooltipRef.current);
        tooltipRef.current = null;
      }
    };
  }, [map, currentMapAnalysisId]); // Add currentMapAnalysisId as dependency to recreate tooltip when data changes

  // Create source and layer when component mounts
  useEffect(() => {
    if (!map) {
      return;
    }

    const vectorSource = new VectorSource();
    setSource(vectorSource);
    
    // Always use a very low z-index to ensure symbol layers appear on top
    // For internally_displaced_persons, use an even lower z-index to allow DTM bubbles to be on top
    const effectiveZIndex = selectedPillarKey === 'internally_displaced_persons' ? 50 : Math.min(100, zIndex);
    
    const layer = new VectorLayer({
      source: vectorSource,
      zIndex: effectiveZIndex, // Use higher z-index for IDP dataset
      // opacity: 0.335, // Set full opacity initially for debugging
      visible: true,
      style: (feature) => {
        // Get feature ID for hover effect
        const featureId = feature.getId() || feature.ol_uid;
        // Only apply hover effect if not hovering over legend
        const isHovered = !isMouseOverLegend && featureId === hoveredFeatureId;
        
        // For internally_displaced_persons dataset, check if we should apply fill effect
        const shouldApplyFill = selectedPillarKey !== 'internally_displaced_persons' || isHovered;
        
        // Default style with conditional opacity for hover
        const defaultStyle = new Style({
          fill: new Fill({
            color: isMouseOverLegend ? 'rgba(255, 255, 255, 0.3)' :
                   (!isMouseOverLegend && isHovered) ? 'rgba(255, 255, 255, 0.3)' :
                   (shouldApplyFill ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0)')
          }),
          stroke: new Stroke({
            color: (!isMouseOverLegend && isHovered) ? 'rgba(100, 100, 100, 0.25)' : 'rgba(100, 100, 100, 0.02)',
            width: isHovered ? 1.25 : 1
          })
        });

        // Add label if showLabels is true
        if (showLabels) {
          defaultStyle.setText(new Text({
            text: feature.get('adm1_name'),
            font: '14px Arial',
            fill: new Fill({
              color: 'rgba(0, 0, 0, 0.8)'
            }),
            stroke: new Stroke({
              color: 'rgba(255, 255, 255, 0.02)',
              width: 3
            }),
            overflow: true,
            offsetY: 0
          }));
        }

        return defaultStyle;
      }
    });

    setVectorLayer(layer);
    
    // Ensure the layer is added to the map and visible
    map.addLayer(layer);
    
    // Force a map render to ensure the layer is displayed, but only if the map still has a target element
    if (map && map.getTargetElement()) {
      map.renderSync();
    }
    


    return () => {
      if (map) {
        // Removing vector layer from map
        map.removeLayer(layer);
      }
    };
  }, [map, zIndex, opacity, showLabels]);

  // Memoize the GeoJSON processing to avoid redundant operations
  const processedFeatures = useMemo(() => {
    if (!geoJsonData) return null;
    
    try {
      // Make sure we're working with the actual GeoJSON object
      const geoJsonObject = typeof geoJsonData === 'string' ? JSON.parse(geoJsonData) : geoJsonData;
      
      // Check if we need to create a proper GeoJSON structure
      let properGeoJson = geoJsonObject;
      
      // If we have an array of features but not a proper GeoJSON object
      if (Array.isArray(geoJsonObject)) {
        properGeoJson = {
          type: 'FeatureCollection',
          features: geoJsonObject
        };
      } else if (!geoJsonObject.type) {
        // If we have individual geometry objects without proper GeoJSON structure
        properGeoJson = {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: geoJsonObject,
            properties: {}
          }]
        };
      }
      
      return new GeoJSON().readFeatures(properGeoJson, {
        featureProjection: 'EPSG:3857'
      });
    } catch (error) {
      console.error('Error processing GeoJSON data:', error);
      return null;
    }
  }, [geoJsonData]);

  // Update source when geoJsonData changes
  useEffect(() => {
    if (!source) {
      return;
    }
    
    if (!processedFeatures) {
      return;
    }

    source.clear();
    
    
    if (processedFeatures.length > 0) {
      
      // Debug: Log properties of first few features to help identify the correct property names
      for (let i = 0; i < Math.min(3, processedFeatures.length); i++) {
        const props = processedFeatures[i].getProperties();
        
        // Look for properties that might contain admin codes
        for (const key in props) {
          if (typeof props[key] === 'string' && 
              (key.toLowerCase().includes('pcode') || 
               key.toLowerCase().includes('admin') || 
               key.toLowerCase().includes('code'))) {
          }
        }
      }
    }
    
    source.addFeatures(processedFeatures);
    
    // Force a refresh of the source
    source.changed();
    if (vectorLayer) {
      // Ensure styles are applied with the latest mapAnalysisData
      vectorLayer.changed();
      
      // Force map render to ensure changes are visible, but only if the map is still mounted
      if (map && map.getTargetElement()) {
        map.render();
      }
    }
  }, [source, processedFeatures, vectorLayer, map, selectedPillarKey, currentMapAnalysisId]);

  // Update vector layer style when data changes
  useEffect(() => {
    
    if (!vectorLayer || !source) {
      // VectorLayer or source not available, skipping style update
      return;
    }
    
    // Force a redraw of the layer with the updated styles
    vectorLayer.changed();
    
    // Force the map to render with new style, but only if the map is still mounted
    if (map && map.getTargetElement()) {
      map.render();
    }
  }, [vectorLayer, mapAnalysisData, currentMapAnalysisId, source, map]);

  // Define hideTooltip at component level with useCallback
  const hideTooltip = useCallback(() => {
    // Ensure tooltip is hidden
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
      tooltipRef.current.style.visibility = 'hidden';
      tooltipRef.current.innerHTML = ''; // Clear content
    }
    
    // Reset cursor style
    if (map && map.getTargetElement()) {
      map.getTargetElement().style.cursor = '';
    }
    
    // Clear hovered feature ID to reset opacity
    setHoveredFeatureId(null);
    
    // Force a redraw of the vector layer to update styles
    if (vectorLayer) {
      vectorLayer.changed();
    }
  }, [map, vectorLayer]);

  // Expose hideTooltip function to parent component
  useEffect(() => {
    if (hideTooltipRef) {
      hideTooltipRef.current = hideTooltip;
    }
    
    return () => {
      if (hideTooltipRef) {
        hideTooltipRef.current = null;
      }
    };
  }, [hideTooltip, hideTooltipRef]);

  // Set up tooltip interaction
  useEffect(() => {
    if (!map || !vectorLayer || !tooltip || !tooltipRef.current) return;
    
    // Clean up previous listeners
    tooltipListeners.forEach(listener => unByKey(listener));
    setTooltipListeners([]);
    

    
    // Reset hover states when setting up new tooltip interaction
    setHoveredFeatureId(null);
    
    // Reset tooltip
    hideTooltip();
    
    // Function to display tooltip
    const displayTooltip = (evt) => {
      if (isMouseOverLegend) {
        hideTooltip();
        return;
      }

      const dtmTooltip = document.querySelector('.dtm-tooltip');
      if (dtmTooltip && dtmTooltip.style.display === 'block') {
        hideTooltip();
        return;
      }

      const legendElement = document.querySelector('.map-legend');
      if (legendElement) {
        const legendRect = legendElement.getBoundingClientRect();
        const mouseX = evt.originalEvent.clientX;
        const mouseY = evt.originalEvent.clientY;
        
        if (mouseX >= legendRect.left && mouseX <= legendRect.right &&
            mouseY >= legendRect.top && mouseY <= legendRect.bottom) {
          hideTooltip();
          return;
        }
      }
      
      const pixel = evt.pixel;
      const feature = map.forEachFeatureAtPixel(pixel, feature => feature, {
        layerFilter: layer => layer === vectorLayer
      });
      
      setHoveredFeatureId(feature ? (feature.getId() || feature.ol_uid) : null);
      map.getTargetElement().style.cursor = feature ? 'pointer' : '';
      
      // Clear hovered pcode when not hovering any feature
      if (!feature && onHoverPcode) {
        onHoverPcode(null);
      }
      
      if (feature) {
        const properties = feature.getProperties();
        const adminName = properties.adm1_name || properties.admin1_name || properties.name || '';
        let adminCode = null;
        const possibleCodeProps = ['adm1_pcode', 'admin1_pcode', 'admin1_id', 'pcode', 'ADM1_PCODE'];
        
        for (const prop of possibleCodeProps) {
          if (properties[prop]) {
            adminCode = properties[prop];
            break;
          }
        }
        
        // Notify parent about hovered pcode for tilemap sync
        if (onHoverPcode && adminCode) {
          onHoverPcode(adminCode);
        }
        
        let severityInfo = '';
        let rationaleInfo = '';
        let additionalInfo = '';
        
        const hasMapAnalysisData = mapAnalysisData && (
          typeof mapAnalysisData === 'object' || 
          typeof mapAnalysisData === 'string' || 
          mapAnalysisData.content // Content can be string or object
        );
        
        if (hasMapAnalysisData) {
          try {
            let parsedData = mapAnalysisData;
            if (typeof mapAnalysisData === 'string') {
              parsedData = JSON.parse(mapAnalysisData);
            } else if (mapAnalysisData.content) {
              // Content can be string (from API) or object (from WebSocket)
              parsedData = typeof mapAnalysisData.content === 'string' 
                ? JSON.parse(mapAnalysisData.content) 
                : mapAnalysisData.content;
            }
            
            let dataArray = [];
            let legendData = null;
            
            if (parsedData.data) {
              dataArray = parsedData.data;
              legendData = parsedData.legend;
            } else if (Array.isArray(parsedData)) {
              dataArray = parsedData;
            } else if (parsedData.analysis_data) {
              dataArray = parsedData.analysis_data;
              legendData = parsedData.legend;
            }
            
            const adminData = dataArray.find(item => {
              const itemCode = item.admin1_pcode || item.admin1_id || item.pcode;
              return itemCode === adminCode || 
                    (adminCode?.startsWith('AF') && itemCode === adminCode.substring(2)) ||
                    (itemCode?.startsWith('AF') && adminCode === itemCode.substring(2));
            });
            
            if (adminData) {
              const severityValue = adminData.value !== undefined ? adminData.value : 
                                  (adminData.category !== undefined ? adminData.category : 
                                  (adminData.severity_score !== undefined ? adminData.severity_score : 
                                  (adminData.severity !== undefined ? adminData.severity : null)));
              
              // Get severity color from legend if available
              let severityColor = null;
              let categoryLabel = '';
              
              if (legendData && severityValue !== undefined) {
                // Try to find color in different legend formats
                if (Array.isArray(legendData)) {
                  const legendItem = legendData.find(item => {
                    // First try exact match on id
                    if (item.id !== undefined && String(item.id) === String(severityValue)) {
                      return true;
                    }
                    // Then try value
                    if (item.value !== undefined && String(item.value) === String(severityValue)) {
                      return true;
                    }
                    // Then try category
                    if (item.category !== undefined && String(item.category) === String(severityValue)) {
                      return true;
                    }
                    // Finally try title for string categories
                    return item.title === severityValue || item.category === severityValue;
                  });

                  if (legendItem) {
                    severityColor = legendItem.color;
                    categoryLabel = legendItem.title || legendItem.description?.split(':')[0].trim() || `${severityValue}`;
                  }
                }
              }
              
              // Create a color box similar to the legend
              const colorBox = severityColor ? 
                `<div style="opacity: 0.30333; display: inline-block; width: 12px; height: 12px; background-color: ${severityColor}; border-radius: 1px; margin-right: 8px; vertical-align: middle; margin-top: 5px; box-shadow: 0 1px 2px rgba(0,0,0,0.01);"></div>` : 
                '';
              
              // Format the severity info to match the legend style without the 'Severity:' label
              severityInfo = `<div style="display: flex; align-items: center; margin: 4px 0; padding: 4px; margin-top: 1px; background-color: rgba(255,255,255,0.95); border-radius: 4px;">
                ${colorBox}<span style="font-weight: 500; font-size: var(--font-size-sm);">${categoryLabel}</span>
              </div>`;
              
              // Add rationale if available
              if (adminData.rationale) {
                // Function to safely escape HTML and remove source references
                const escapeHTML = (str) => {
                  // First remove source references like [1], [2], etc.
                  let cleanStr = str.replace(/\[\d+\]/g, '');
                  // Also remove multiple spaces that might be left after removing references
                  cleanStr = cleanStr.replace(/\s+/g, ' ').trim();
                  
                  return cleanStr
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;')
                    .replace(/\n/g, '<br>');
                };
                
                // Always show the full rationale text
                const rationaleText = escapeHTML(adminData.rationale);
                
                // Just store the rationale text, we'll format it in the tooltip HTML
                rationaleInfo = rationaleText;
              }
              
              // Add any additional data fields that might be useful
              if (adminData.population) {
                additionalInfo += `<div><strong>Population:</strong> ${adminData.population.toLocaleString()}</div>`;
              }
              
              // Add confidence level if available
              if (adminData.confidence_level) {
                const confidenceText = adminData.confidence_level.replace('Confidence level - ', '');
                const confidenceColor = confidenceText.toLowerCase() === 'high' ? '#28a745' : 
                                       confidenceText.toLowerCase() === 'medium' ? '#ffc107' : '#dc3545';
                additionalInfo += `<div><strong>Confidence:</strong> <span style="color: ${confidenceColor}; font-weight: bold;">${confidenceText}</span></div>`;
              }
              
              // Add evidence if available (truncated for tooltip)
              if (adminData.evidence) {
                const evidenceText = adminData.evidence.length > 150 ? 
                  `${adminData.evidence.substring(0, 150)}...` : adminData.evidence;
                additionalInfo += `<div style="margin-top: 4px;"><strong>Evidence:</strong> <span style="font-style: italic; color: #666;">${evidenceText}</span></div>`;
              }
              
              // Add sources if available (truncated for tooltip)
              if (adminData.sources) {
                const sourcesText = adminData.sources.length > 100 ? 
                  `${adminData.sources.substring(0, 100)}...` : adminData.sources;
                additionalInfo += `<div style="margin-top: 4px;"><strong>Sources:</strong> <span style="font-size: 0.85em; color: #666;">${sourcesText}</span></div>`;
              }
              
              // Add description/notes if available and rationale is not
              if (!adminData.rationale && (adminData.description || adminData.notes)) {
                const notes = adminData.description || adminData.notes;
                // Use the same HTML structure as for the rationale field
                const notesText = notes.length > 100 ? `${notes.substring(0, 100)}...` : notes;
                // Store in rationaleInfo to use the same formatting as the rationale field
                rationaleInfo = notesText;
              }
            }
          } catch (error) {
            console.error('Error getting data for tooltip:', error);
          }
        }
        
        // Set tooltip content and position
        if (adminName) {
          // Check if there's actual data for this polygon
          const hasActualData = severityInfo || rationaleInfo || additionalInfo;
          
          // Format the timestamp if available AND there's actual data
          let timestampHtml = '';
          if (hasMapAnalysisData && mapAnalysisData.updated_at && hasActualData) {
            try {
              const date = new Date(mapAnalysisData.updated_at);
              const timeAgo = formatDistanceToNow(date, { addSuffix: true }).replace('about ', '');
              
              timestampHtml = `
                <div style="display: flex; align-items: center; margin-top: 8px; background-color: rgba(0, 0, 0, 0.03); border-radius: 12px; padding: 2px 8px; border: 1px solid rgba(0, 0, 0, 0.05); width: fit-content;">
                  <span style="margin-right: 4px; display: inline-flex;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </span>
                  <span style="font-size: var(--font-size-md); opacity: 0.7; font-style: italic;">${timeAgo}</span>
                </div>`;
            } catch (e) {
              console.error('Error formatting date in tooltip:', e);
            }
          } else if (mapUpdatedAt && hasActualData) {
            try {
              const timeAgo = formatDistanceToNow(mapUpdatedAt, { addSuffix: true }).replace('about ', '');
              
              timestampHtml = `
                <div style="display: flex; align-items: center; margin-top: 8px; background-color: rgba(0, 0, 0, 0.03); border-radius: 12px; padding: 2px 8px; border: 1px solid rgba(0, 0, 0, 0.05); width: fit-content;">
                  <span style="margin-right: 4px; display: inline-flex;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </span>
                  <span style="font-size: var(--font-size-md); opacity: 0.7; font-style: italic;">${timeAgo}</span>
                </div>`;
            } catch (e) {
              console.error('Error formatting date in tooltip:', e);
            }
          }
          
          tooltipRef.current.innerHTML = `
            <div class="tooltip-title">${adminName}</div>
            ${adminCode ? `<div><strong>Code:</strong> ${adminCode}</div>` : ''}
            ${severityInfo}
            ${rationaleInfo ? `
              <div class="tooltip-rationale">
                <strong>Rationale:</strong>
                <div class="rationale-content">${rationaleInfo}</div>
              </div>
            ` : ''}
            ${additionalInfo}
            ${timestampHtml}
          `;
          
          // Get tooltip dimensions
          const tooltipWidth = 280; // Fixed width for tooltip (reduced)
          
          // Calculate mouse position in the window
          const mouseX = evt.originalEvent.clientX;
          const mouseY = evt.originalEvent.clientY;
          
          // Get window dimensions
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;
          
          // Show tooltip to measure its height
          tooltipRef.current.style.visibility = 'hidden'; // Make it invisible but measurable
          tooltipRef.current.style.display = 'block';
          
          // Get actual tooltip height after content is rendered
          const tooltipHeight = tooltipRef.current.offsetHeight;
          
          // Calculate the best position for the tooltip
          let left, top;
          
          // Horizontal positioning
          if (mouseX + tooltipWidth + 20 > windowWidth) {
            // Too close to right edge, position to the left of the cursor
            left = mouseX - tooltipWidth - 10;
          } else {
            // Position to the right of the cursor
            left = mouseX + 15;
          }
          
          // If still off-screen on the left, position centered below cursor
          if (left < 10) {
            left = Math.max(10, mouseX - (tooltipWidth / 2));
          }
          
          // Vertical positioning
          if (mouseY + tooltipHeight + 20 > windowHeight) {
            // Too close to bottom edge, position above cursor
            top = mouseY - tooltipHeight - 10;
          } else {
            // Position below cursor
            top = mouseY + 15;
          }
          
          // If still off-screen on the top, position at top with margin
          if (top < 10) {
            top = 10;
          }
          
          // Apply the calculated position
          tooltipRef.current.style.left = `${left}px`;
          tooltipRef.current.style.top = `${top}px`;
          
          // Make sure the tooltip is visible and on top
          tooltipRef.current.style.visibility = 'visible';
          tooltipRef.current.style.zIndex = '99999';
          
          // Force a reflow to ensure proper positioning
          void tooltipRef.current.offsetHeight;
          
          // Add a slight delay to ensure proper rendering
          setTimeout(() => {
            if (tooltipRef.current && tooltipRef.current.style.display === 'block') {
              // Reapply position in case content changed size
              tooltipRef.current.style.left = `${left}px`;
              tooltipRef.current.style.top = `${top}px`;
            }
          }, 10);
        } else {
          tooltipRef.current.style.display = 'none';
        }
      } else {
        tooltipRef.current.style.display = 'none';
      }
    };
    
    // Register event listeners
    const moveListener = map.on('pointermove', displayTooltip);
    const outListener = map.on('pointerout', hideTooltip);
    
    // Store listeners for cleanup
    setTooltipListeners([moveListener, outListener]);
    
    return () => {
      // Remove event listeners
      unByKey(moveListener);
      unByKey(outListener);
      
      // Hide tooltip
      hideTooltip();
      
      // Reset cursor just to be safe
      if (map && map.getTargetElement()) {
        map.getTargetElement().style.cursor = '';
      }
    };
  }, [map, vectorLayer, tooltip, isMouseOverLegend, mapAnalysisData, selectedPillarKey, currentMapAnalysisId, hideTooltip]);

  // Update styles when mapAnalysisData, hoveredSeverity, or hoveredFeatureId changes
  useEffect(() => {
    if (!vectorLayer || !source) return;

    try {
      if (!mapAnalysisData?.content) {
        vectorLayer.setStyle((feature) => {
          const featureId = feature.getId() || feature.ol_uid;
          const isHovered = !isMouseOverLegend && featureId === hoveredFeatureId;
          
          return new Style({
            fill: new Fill({
              color: 'rgba(255, 255, 255, 0)'
            }),
            stroke: new Stroke({
              color: isHovered ? 'rgba(100, 100, 100, 0.15)' : 'rgba(100, 100, 100, 0.08)',
              width: isHovered ? 1.15 : 1
            }),
            text: showLabels ? new Text({
              text: feature.get('adm1_name'),
              font: '14px Arial',
              fill: new Fill({
                color: 'rgba(0, 0, 0, 0.8)'
              }),
              stroke: new Stroke({
                color: 'rgba(255, 255, 255, 0.08)',
                width: 1
              }),
              overflow: true,
              offsetY: 0
            }) : null
          });
        });
        return;
      }

      let parsedData = mapAnalysisData;
      let dataArray = [];
      let colorMap = {};

      if (typeof mapAnalysisData === 'string') {
        parsedData = JSON.parse(mapAnalysisData);
      } else if (mapAnalysisData.content) {
        parsedData = JSON.parse(mapAnalysisData.content);
      }

      if (parsedData.data) {
        dataArray = parsedData.data;
        if (Array.isArray(parsedData.legend)) {
          parsedData.legend.forEach(item => {
            if (item.color) { // Ensure color exists
              // Map by ID if present
              if (item.id !== undefined) {
                colorMap[String(item.id)] = item.color;
              }
              // Map by title if present
              if (item.title !== undefined) {
                colorMap[String(item.title)] = item.color;
              }
              // Fallback: Map by value if present and different from id/title
              if (item.value !== undefined &&
                  (item.id === undefined || String(item.value) !== String(item.id)) &&
                  (item.title === undefined || String(item.value) !== String(item.title))) {
                colorMap[String(item.value)] = item.color;
              }
            }
          });
        }
      } else if (Array.isArray(parsedData)) {
        dataArray = parsedData;
      }

      const severityMap = new Map();
      dataArray.forEach(item => {
        let severityValueFromDataItem = null;
        
        if (item.category !== undefined) {
          severityValueFromDataItem = item.category; // Can be string or number
        } else if (item.value !== undefined) {
          severityValueFromDataItem = item.value;
        } else if (item.severity_score !== undefined) {
          severityValueFromDataItem = item.severity_score;
        }
        // No conversion of severityValueFromDataItem to Number here.
        
        let adminCode = item.admin1_pcode || item.admin1_id || item.pcode;
        
        if (adminCode && severityValueFromDataItem !== undefined) {
          severityMap.set(adminCode, severityValueFromDataItem);
          if (adminCode.startsWith('AF')) { // Specific handling for 'AF' prefix
            severityMap.set(adminCode.substring(2), severityValueFromDataItem);
          }
        }
      });

      vectorLayer.setStyle((feature) => {
        const featureId = feature.getId() || feature.ol_uid;
        const properties = feature.getProperties();
        
        let admin1Pcode = null;
        const possibleCodeProps = ['adm1_pcode', 'admin1_pcode', 'admin1_id', 'pcode', 'ADM1_PCODE'];
        
        for (const prop of possibleCodeProps) {
          if (properties[prop]) {
            admin1Pcode = properties[prop];
            break;
          }
        }
        
        // Check if this polygon is highlighted via tilemap pcode hover
        const isHoveredByPcode = hoveredPcode && admin1Pcode && 
          (admin1Pcode === hoveredPcode || admin1Pcode.endsWith(hoveredPcode) || hoveredPcode.endsWith(admin1Pcode));
        const isHoveredDirectly = !isMouseOverLegend && (featureId === hoveredFeatureId || isHoveredByPcode);

        const severityScoreFromMap = severityMap.get(admin1Pcode); // Value from data (string or number)
        let fillColor = 'rgba(220, 220, 220, 0.04)'; // Default fill
        let calculatedOpacity = 0.222; // Default opacity
        
        if (severityScoreFromMap !== undefined) {
          if (hoveredSeverity !== null) {
            let isMatchedToLegendHover = false;
            const legendArrayForStyle = (parsedData.legend && Array.isArray(parsedData.legend)) ? parsedData.legend : [];

            const targetLegendItem = legendArrayForStyle.find(legItem =>
              robustCompare(legItem.id, hoveredSeverity) ||
              robustCompare(legItem.value, hoveredSeverity) ||
              robustCompare(legItem.title, hoveredSeverity)
            );

            if (targetLegendItem) {
              isMatchedToLegendHover = robustCompare(severityScoreFromMap, targetLegendItem.id) ||
                                     robustCompare(severityScoreFromMap, targetLegendItem.value) ||
                                     robustCompare(severityScoreFromMap, targetLegendItem.title);
            } else {
              isMatchedToLegendHover = robustCompare(severityScoreFromMap, hoveredSeverity);
            }
            calculatedOpacity = isMatchedToLegendHover ? 0.30333 : 0.0025;
          }

          const severityKeyForColorMap = String(severityScoreFromMap); // Use string for lookup
          if (colorMap[severityKeyForColorMap]) {
            let color = colorMap[severityKeyForColorMap];
            if (color.startsWith('#')) {
              const r = parseInt(color.slice(1, 3), 16);
              const g = parseInt(color.slice(3, 5), 16);
              const b = parseInt(color.slice(5, 7), 16);
              fillColor = `rgba(${r}, ${g}, ${b}, ${calculatedOpacity})`;
            } else if (color.startsWith('rgb')) { // Handles existing rgb or rgba
              if (!color.startsWith('rgba')) { // Convert rgb to rgba
                color = color.replace('rgb', 'rgba').replace(')', `, ${calculatedOpacity})`);
              } else { // Adjust opacity for existing rgba
                color = color.replace(/rgba\([^,]+,[^,]+,[^,]+,[^)]+\)/, (match) => {
                  return match.replace(/,[^)]+\)$/, `, ${calculatedOpacity})`);
                });
              }
              fillColor = color;
            } else { // Fallback if color format is unknown (should not happen with hex)
              fillColor = color; // Use as is, though might not be ideal
            }
          }
        }

        const fillOpacityForHover = isHoveredDirectly ? 0.30333 : calculatedOpacity; // Use calculatedOpacity if not directly hovered
        
        if (isHoveredDirectly && fillColor.includes('rgba')) {
           fillColor = fillColor.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${fillOpacityForHover})`);
        } else if (isHoveredDirectly && !fillColor.includes('rgba') && fillColor !== 'rgba(220, 220, 220, 0.04)') {
          // This case remains tricky for non-rgba, non-default colors.
          // For now, direct hover on such items won't change opacity if color isn't parsed to RGBA.
        }

        // If not directly hovered, but opacity was set by legend match, ensure fillColor reflects that calculatedOpacity
        if (!isHoveredDirectly && fillColor.includes('rgba')) {
            fillColor = fillColor.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${calculatedOpacity})`);
        }

        return new Style({
          fill: new Fill({
            color: fillColor
          }),
          stroke: new Stroke({
            color: isHoveredDirectly ? 'rgba(70, 70, 70, 0.222)' : 'rgba(100, 100, 100, 0.08)',
            width: isHoveredDirectly ? 1.2 : 1
          }),
          text: showLabels ? new Text({
            text: feature.get('adm1_name'),
            font: '14px Arial',
            fill: new Fill({
              color: 'rgba(0, 0, 0, 0.8)'
            }),
            stroke: new Stroke({
              color: 'rgba(255, 255, 255, 0.08)',
              width: 3
            }),
            overflow: true,
            offsetY: 0
          }) : null
        });
      });
    } catch (error) {
      // Silent error handling for style updates
    }
  }, [vectorLayer, mapAnalysisData, source, hoveredSeverity, hoveredPcode, hoveredFeatureId, isMouseOverLegend, selectedPillarKey, currentMapAnalysisId]);

  // Force layer refresh when data changes
  useEffect(() => {
    if (vectorLayer && source) {
      
      // Clear the layer to remove old styles
      if (source.getFeatures().length > 0) {
        // Only reset the styling, don't clear features
        vectorLayer.changed();
      }
      
      // Clear tooltips when data changes
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
        tooltipRef.current.style.visibility = 'hidden';
        tooltipRef.current.innerHTML = '';
      }
      
      // Reset hover states
      setHoveredFeatureId(null);
      
      // Force refresh of map to ensure changes are visible, but only if the map is still mounted
      if (map && map.getTargetElement()) {
        map.render();
      }
    }
  }, [vectorLayer, source, map, mapAnalysisData, selectedPillarKey, currentMapAnalysisId]);

  // Add this to track pillar changes
  const prevPillarRef = useRef(selectedPillarKey);
  
  // Gentle refresh when pillar changes - avoid clearing/re-adding features
  useEffect(() => {
    // Only run if we already have a layer and the pillar actually changed
    if (vectorLayer && prevPillarRef.current !== selectedPillarKey) {
      
      // Clear any existing tooltips
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
        tooltipRef.current.style.visibility = 'hidden';
        tooltipRef.current.innerHTML = '';
      }
      
      // Reset hover states
      setHoveredFeatureId(null);
      
      // Instead of clearing/re-adding features, just refresh the layer styling
      if (vectorLayer) {
        // Force style recalculation by refreshing the layer
        vectorLayer.changed();
        
        // Single gentle map render to update visuals, but only if the map is still mounted
        if (map && map.getTargetElement()) {
          map.renderSync();
        }
      }
    }
    
    // Update the ref for the next comparison
    prevPillarRef.current = selectedPillarKey;
  }, [selectedPillarKey, vectorLayer, map]);

  // Memoize color map and severity map
  const colorMap = useMemo(() => {
    if (!mapAnalysisData?.legend) return new Map();
    return createColorMap(mapAnalysisData.legend);
  }, [mapAnalysisData?.legend]);

  const severityMap = useMemo(() => {
    return createSeverityMap(mapAnalysisData);
  }, [mapAnalysisData]);

  // Update the style function to use memoized maps
  const getFeatureStyle = useCallback((feature) => {
    const featureId = feature.getId() || feature.ol_uid;
    const isHovered = !isMouseOverLegend && featureId === hoveredFeatureId;
    const properties = feature.getProperties();
    
    let admin1Pcode = null;
    const possibleCodeProps = ['adm1_pcode', 'admin1_pcode', 'admin1_id', 'pcode', 'ADM1_PCODE'];
    
    for (const prop of possibleCodeProps) {
      if (properties[prop]) {
        admin1Pcode = properties[prop];
        break;
      }
    }

    const severityScore = severityMap.get(admin1Pcode);
    let fillColor = 'rgba(220, 220, 220, 0.04)';
    let opacity = 0.222;
    
    if (severityScore !== undefined) {
      const color = colorMap.get(severityScore);
      if (color) {
        fillColor = `rgba(${hexToRgb(color)}, ${opacity})`;
      }
    }

    const style = new Style({
      fill: new Fill({
        color: isMouseOverLegend ? 'rgba(255, 255, 255, 0.3)' :
               (!isMouseOverLegend && isHovered) ? 'rgba(255, 255, 255, 0.3)' :
               fillColor
      }),
      stroke: new Stroke({
        color: (!isMouseOverLegend && isHovered) ? 'rgba(100, 100, 100, 0.25)' : 'rgba(100, 100, 100, 0.02)',
        width: isHovered ? 1.25 : 1
      })
    });

    if (showLabels) {
      style.setText(new Text({
        text: feature.get('adm1_name'),
        font: '14px Arial',
        fill: new Fill({
          color: 'rgba(0, 0, 0, 0.8)'
        }),
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 0.08)',
          width: 3
        }),
        overflow: true,
        offsetY: 0
      }));
    }

    return style;
  }, [severityMap, colorMap, hoveredFeatureId, isMouseOverLegend, showLabels]);

  return null;
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  return (
    prevProps.selectedPillarKey === nextProps.selectedPillarKey &&
    prevProps.mapAnalysisData === nextProps.mapAnalysisData &&
    prevProps.hoveredSeverity === nextProps.hoveredSeverity &&
    prevProps.hoveredPcode === nextProps.hoveredPcode &&
    prevProps.isMouseOverLegend === nextProps.isMouseOverLegend &&
    prevProps.mapUpdatedAt === nextProps.mapUpdatedAt &&
    prevProps.geoJsonData === nextProps.geoJsonData
  );
});

Admin1Layer.displayName = 'Admin1Layer';

export default Admin1Layer;
